import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import data from "../../data/index.json";
import { useTranslation } from "react-i18next";
import {
  HiExternalLink,
  HiCalendar,
  HiChevronRight,
  HiChevronLeft,
  HiPlay,
  HiPause,
} from "react-icons/hi";

// Brand-palette accent per project
const ACCENT_COLORS = [
  "#f9c70f", // brand yellow
  "#94bff4", // accent blue
  "#605d5d", // secondary gray
  "#f9c70f",
  "#94bff4",
  "#605d5d",
  "#f9c70f",
];

function Showcase() {
  const [selected, setSelected] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const showcase = data.showcase;
  const { t } = useTranslation();

  const total = showcase.length;
  const accent = ACCENT_COLORS[selected] || "#f9c70f";

  // Auto-rotate
  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      setDirection(1);
      setSelected((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(timer);
  }, [isPlaying, total]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        setDirection(1);
        setSelected((prev) => (prev + 1) % total);
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        setDirection(-1);
        setSelected((prev) => (prev - 1 + total) % total);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [total]);

  // Update sidebar underline tracker
  useEffect(() => {
    const underline = document.querySelector(".underline-track");
    const selectedItem = document.querySelector(".exp-slider-item-selected");
    if (underline && selectedItem) {
      underline.style.transition = "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)";
      underline.style.height = `${selectedItem.offsetHeight}px`;
      underline.style.top = `${selectedItem.offsetTop}px`;
    }
  }, [selected]);

  const goNext = useCallback(() => {
    setDirection(1);
    setSelected((prev) => (prev + 1) % total);
    setIsPlaying(false);
  }, [total]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setSelected((prev) => (prev - 1 + total) % total);
    setIsPlaying(false);
  }, [total]);

  const goTo = (i) => {
    setDirection(i > selected ? 1 : -1);
    setSelected(i);
    setIsPlaying(false);
  };

  const slideVariants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -40 : 40 }),
  };

  return (
    <div
      className="relative w-full py-28 bg-surface overflow-hidden"
      onMouseEnter={() => setIsPlaying(false)}
      onMouseLeave={() => setIsPlaying(true)}
    >
      {/* Grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(to right, #e2e8f0 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          opacity: 0.35,
        }}
      />

      {/* Watermark */}
      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10rem] font-black text-gray-100 select-none pointer-events-none leading-none hidden xl:block">
        WORK
      </span>

      <div className="relative z-10 max-w-6xl mx-auto px-6">

        {/* Section Header */}
        <motion.div
          className="mb-14"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-bold tracking-widest uppercase text-secondary mb-5 mx-auto block w-fit">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
            {t("Impact in Action")}
          </span>

          <h2 className="text-5xl md:text-6xl font-black text-gray-900 tracking-tight text-center mb-4">
            {t("Client")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gray to-black-color">
              {t("Engagements")}
            </span>
          </h2>
          <div
            className="w-20 h-[2px] mx-auto rounded-full"
            style={{ background: "linear-gradient(90deg, #605d5d, #111827)" }}
          />
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8 w-full">

          {/* ── Sidebar Tab List ── */}
          <motion.div
            className="relative lg:min-w-[260px]"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            {/* Track line */}
            <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gray-200 rounded-full hidden lg:block" />
            {/* Active indicator */}
            <div
              className="underline-track absolute left-0 w-[3px] rounded-full hidden lg:block"
              style={{ background: accent, transition: "background 0.4s ease, top 0.35s ease, height 0.35s ease" }}
            />

            <ul className="relative flex flex-row lg:flex-col gap-1.5 overflow-x-auto lg:overflow-x-visible pb-3 lg:pb-0">
              {showcase.map((item, index) => {
                const itemAccent = ACCENT_COLORS[index] || "#605d5d";
                const isActive = index === selected;
                return (
                  <li
                    key={item.name}
                    onClick={() => goTo(index)}
                    className={`exp-slider-item${isActive ? "-selected" : ""} relative pl-5 pr-4 py-4 cursor-pointer rounded-lg transition-all duration-250 text-sm font-semibold select-none flex items-center gap-3 whitespace-nowrap lg:whitespace-normal ${isActive ? "bg-white shadow-sm" : "hover:bg-white/60"}`}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0 transition-all duration-300"
                      style={{ background: isActive ? itemAccent : "#d1d5db" }}
                    />
                    <span
                      className="transition-colors duration-200"
                      style={{ color: isActive ? "#111827" : "#9ca3af" }}
                    >
                      {item.name}
                    </span>
                    {isActive && (
                      <HiChevronRight className="ml-auto text-gray-300 hidden lg:block flex-shrink-0" />
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Auto-play progress bar — desktop only */}
            <div className="hidden lg:block mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={() => setIsPlaying((p) => !p)}
                  className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-secondary transition-all duration-200"
                  title={isPlaying ? "Pause auto-rotate" : "Resume auto-rotate"}
                >
                  {isPlaying
                    ? <HiPause className="text-secondary text-xs" />
                    : <HiPlay className="text-secondary text-xs" />}
                </button>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {isPlaying ? "Auto-rotating" : "Paused"}
                </span>
              </div>
              {/* Progress track */}
              <div className="h-[2px] w-full bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  key={`${selected}-${isPlaying}`}
                  className="h-full rounded-full"
                  style={{ background: accent }}
                  initial={{ width: "0%" }}
                  animate={{ width: isPlaying ? "100%" : "0%" }}
                  transition={{ duration: isPlaying ? 5 : 0, ease: "linear" }}
                />
              </div>
            </div>
          </motion.div>

          {/* ── Content Panel ── */}
          <div className="flex-1 flex flex-col gap-4">

            {/* Prev / Next controls */}
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-gray-300 uppercase tracking-widest">
                {String(selected + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={goPrev}
                  className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center hover:border-secondary hover:bg-secondary/5 transition-all duration-200 shadow-sm"
                >
                  <HiChevronLeft className="text-gray-500 text-base" />
                </button>
                <button
                  onClick={goNext}
                  className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center hover:border-secondary hover:bg-secondary/5 transition-all duration-200 shadow-sm"
                >
                  <HiChevronRight className="text-gray-500 text-base" />
                </button>
              </div>
            </div>

            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={selected}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="relative bg-white border border-gray-100 rounded-2xl p-8 shadow-sm overflow-hidden"
              >
                {/* Dynamic top accent bar */}
                <div
                  className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl"
                  style={{ background: accent, transition: "background 0.4s ease" }}
                />

                {/* Ambient glow */}
                <div
                  className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none"
                  style={{
                    background: `radial-gradient(circle, ${accent}10 0%, transparent 70%)`,
                    transform: "translate(30%, -30%)",
                    transition: "background 0.4s ease",
                  }}
                />

                {/* Role & Company */}
                <div className="relative mb-6">
                  <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight mb-2 leading-tight">
                    {t(showcase[selected].role)}
                    <span style={{ color: accent, transition: "color 0.3s" }}>
                      {" "}@{" "}
                      {showcase[selected].url ? (
                        <a
                          href={showcase[selected].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline underline-offset-2 inline-flex items-center gap-1"
                        >
                          {showcase[selected].name}
                          <HiExternalLink className="text-base inline" />
                        </a>
                      ) : (
                        showcase[selected].name
                      )}
                    </span>
                  </h3>

                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600 mt-1"
                    style={{
                      background: `${accent}10`,
                      borderColor: `${accent}30`,
                      transition: "background 0.3s, border-color 0.3s",
                    }}
                  >
                    <HiCalendar style={{ color: accent }} />
                    {showcase[selected].start} — {showcase[selected].end}
                  </div>
                </div>

                {/* Divider */}
                <div className="w-full h-px bg-gray-50 mb-6" />

                {/* Description bullets */}
                <ul className="space-y-4">
                  {showcase[selected].shortDescription.map((desc, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                      className="flex items-start gap-3 text-gray-600 leading-relaxed text-sm"
                    >
                      <span
                        className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full"
                        style={{ background: accent }}
                      />
                      <span>{t(desc)}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* Dot navigator */}
                <div className="mt-8 pt-4 border-t border-gray-50 flex items-center justify-end">
                  <div className="flex gap-1.5">
                    {showcase.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => goTo(i)}
                        className="h-1.5 rounded-full transition-all duration-300"
                        style={{
                          width: i === selected ? "24px" : "8px",
                          background: i === selected ? accent : "#e5e7eb",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Showcase;
